import { useEffect, useMemo, useRef, useState } from 'react'
import type { MouseEvent } from 'react'
import { Check } from 'lucide-react'
import { detailingServices } from '../../data/ceramic-booking'
import {
  detailingQuoteHref,
  selectedDetailingServiceLabels,
  type DetailingPageInterestKey,
} from '../../lib/detailing-quote'
import { getServicePageJsonLd } from '../../lib/seo'
import { PageShell } from '../layout/page-shell'
import { PageTitle } from '../seo/page-title'
import { ButtonLink } from '../ui/button'

export interface DetailingPageProps {}

const compactServiceCopy: Record<string, { summary: string; highlights: string[] }> = {
  'exterior-detailing': {
    summary: 'Restore gloss, remove contamination and refresh the exterior.',
    highlights: ['Safe wash', 'Wheels & tyres', 'Decontamination'],
  },
  'premium-detailing': {
    summary: 'A fuller enhancement that can include interior work when the vehicle needs it.',
    highlights: ['Exterior reset', 'Interior as part of the job', 'Prepared for protection'],
  },
  'paint-protection': {
    summary: 'Make the finish easier to maintain with professional paint protection.',
    highlights: ['GYEON ceramic', 'Glass protection', 'Wheel protection'],
  },
}

function toggleInterest(current: DetailingPageInterestKey[], key: DetailingPageInterestKey) {
  return current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
}

export function DetailingPage(_: Readonly<DetailingPageProps>) {
  const jsonLd = getServicePageJsonLd('detailing')
  const [selectedInterests, setSelectedInterests] = useState<DetailingPageInterestKey[]>([])
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [inFlowVisible, setInFlowVisible] = useState(true)
  const continueRef = useRef<HTMLDivElement>(null)
  const selectedLabels = useMemo(
    () => selectedDetailingServiceLabels(selectedInterests),
    [selectedInterests],
  )
  const selectedCount = selectedLabels.length
  const hasSelection = selectedCount > 0
  const quoteHref = useMemo(() => detailingQuoteHref(selectedInterests), [selectedInterests])
  const recommendHref = detailingQuoteHref(['unsure'])
  const continueHref = hasSelection ? quoteHref : recommendHref
  const paintProtectionSelected = selectedInterests.includes('paint-protection')
  const showStickyCta = hasSelection && !inFlowVisible

  useEffect(() => {
    if (!hasSelection) {
      setInFlowVisible(true)
      return
    }
    const panel = continueRef.current
    if (!panel) return
    const continuePanel = panel

    function updateVisibility() {
      const rect = continuePanel.getBoundingClientRect()
      setInFlowVisible(rect.top < window.innerHeight - 48 && rect.bottom > 72)
    }

    updateVisibility()
    window.addEventListener('scroll', updateVisibility, { passive: true })
    window.addEventListener('resize', updateVisibility)
    const observer = new IntersectionObserver(updateVisibility, { threshold: [0, 0.35, 1] })
    observer.observe(continuePanel)
    return () => {
      window.removeEventListener('scroll', updateVisibility)
      window.removeEventListener('resize', updateVisibility)
      observer.disconnect()
    }
  }, [hasSelection])

  function toggleIncluded(event: MouseEvent<HTMLButtonElement>, serviceId: string) {
    event.preventDefault()
    event.stopPropagation()
    setExpandedId((current) => (current === serviceId ? null : serviceId))
  }

  return (
    <PageShell>
      <PageTitle title="Car Detailing Geelong | Autoglow Detailing" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <section className="packages-hero">
        <img
          src="/images/detailing-packages.jpg"
          alt="Vehicle paintwork after professional studio detailing."
          width="2560"
          height="1440"
        />
        <div className="packages-hero-content">
          <p className="eyebrow">Tailored Vehicle Detailing</p>
          <h1>Car Detailing Geelong</h1>
          <p>
            Exterior detailing and premium detailing &amp; enhancement, recommended for your vehicle
            rather than a fixed package menu. Paint correction lives on its own page.
          </p>
          <div>
            <ButtonLink href="#detailing-continue">Choose Your Interests</ButtonLink>
            <ButtonLink href="/paint-correction" variant="ghost">Paint Correction</ButtonLink>
          </div>
        </div>
      </section>

      <section className="detailing-section" id="detailing-services" aria-labelledby="detailing-full-heading">
        <div className="section-container">
          <div className="split-section-heading">
            <div>
              <p className="eyebrow">Tailored Assessment</p>
              <h2 id="detailing-full-heading">What does your vehicle need?</h2>
            </div>
            <div>
              <p>
                Select any areas you’re interested in, or leave it to us and we’ll recommend the
                right detailing after inspection.
              </p>
              <p className="detailing-trust-copy">
                Every vehicle is assessed first. Complimentary inspection is included before any
                recommendation is confirmed.
              </p>
            </div>
          </div>
          <div className="detailing-card-grid">
            {detailingServices.map((service) => {
              const selected = selectedInterests.includes(service.id as DetailingPageInterestKey)
              const compact = compactServiceCopy[service.id]
              const expanded = expandedId === service.id
              const includedId = `${service.id}-included`
              return (
                <article
                  className={selected ? 'detailing-compact-card is-selected' : 'detailing-compact-card'}
                  key={service.id}
                >
                  <button
                    aria-pressed={selected}
                    className="detailing-compact-select"
                    onClick={() => setSelectedInterests((current) => (
                      toggleInterest(current, service.id as DetailingPageInterestKey)
                    ))}
                    type="button"
                  >
                    <span className="detailing-interest-state">
                      {selected ? <Check aria-hidden="true" size={14} /> : null}
                      {selected ? 'Selected' : 'Select'}
                    </span>
                    <h3>{service.name}</h3>
                    <p>{compact?.summary}</p>
                    {compact ? (
                      <ul className="detailing-highlights">
                        {compact.highlights.map((item) => <li key={item}>{item}</li>)}
                      </ul>
                    ) : null}
                  </button>
                  <button
                    aria-controls={includedId}
                    aria-expanded={expanded}
                    className="detailing-included-toggle"
                    onClick={(event) => toggleIncluded(event, service.id)}
                    type="button"
                  >
                    What’s included
                  </button>
                  <div className="detailing-included-panel" hidden={!expanded} id={includedId}>
                    <p>{service.description}</p>
                    {service.benefits ? (
                      <ul>
                        {service.benefits.map((benefit) => <li key={benefit}>{benefit}</li>)}
                      </ul>
                    ) : null}
                    {service.options ? (
                      <ul>
                        {service.options.map((option) => <li key={option}>{option}</li>)}
                      </ul>
                    ) : null}
                  </div>
                </article>
              )
            })}
          </div>

          <div
            aria-labelledby="detailing-continue-heading"
            className={hasSelection ? 'detailing-continue-panel is-selected' : 'detailing-continue-panel'}
            id="detailing-continue"
            ref={continueRef}
            role="region"
          >
            {hasSelection ? (
              <>
                <div>
                  <p className="detailing-continue-count" id="detailing-continue-heading">
                    {selectedCount} {selectedCount === 1 ? 'service' : 'services'} selected
                  </p>
                  <p className="detailing-selected-line">{selectedLabels.join(' · ')}</p>
                </div>
                <div className="detailing-continue-actions">
                  <ButtonLink href={continueHref}>Continue to Tailored Quote</ButtonLink>
                  {paintProtectionSelected ? (
                    <a className="detailing-ceramic-crossover" href="/ceramic-protection">
                      Explore Ceramic Protection
                    </a>
                  ) : null}
                </div>
              </>
            ) : (
              <>
                <div>
                  <p className="detailing-continue-count" id="detailing-continue-heading">
                    Not sure what your vehicle needs?
                  </p>
                  <p className="detailing-selected-line">
                    Leave the cards unselected and we’ll recommend the right detailing after inspection.
                  </p>
                </div>
                <div className="detailing-continue-actions">
                  <ButtonLink href={recommendHref}>Get a Recommendation</ButtonLink>
                </div>
              </>
            )}
          </div>

          <p className="detailing-vehicle-strip">
            <span>We work on</span>
            Cars &amp; SUVs · Utes &amp; Vans · Commercial Vehicles · Caravans · Motorbikes
          </p>
          <p className="detailing-gallery-support">
            <ButtonLink href="/paint-correction" variant="ghost">View Paint Correction</ButtonLink>
            <ButtonLink href="/gallery" variant="ghost">View Full Gallery</ButtonLink>
          </p>
        </div>
      </section>

      {hasSelection ? (
        <div
          aria-hidden={!showStickyCta}
          className={showStickyCta ? 'detailing-continue-sticky is-visible' : 'detailing-continue-sticky'}
        >
          <a href={continueHref}>
            Continue — {selectedCount} selected
          </a>
        </div>
      ) : null}
    </PageShell>
  )
}

export default DetailingPage
